// approvalUtils.js — Document approval flow utilities
import { updateData } from './firestoreService';
import { serverTimestamp } from 'firebase/firestore';
import { logAuditEventAsync, AuditActions } from './auditLogger';
import { APPROVAL_EDIT_BEHAVIORS } from '../constants/documentEditorConstants';

/**
 * Default approval config used when company has no config or feature is disabled.
 */
export const DEFAULT_APPROVAL_CONFIG = {
  enabled: false,
  requiredReviewSections: [],
  editBehavior: APPROVAL_EDIT_BEHAVIORS.AUTO_UNAPPROVE,
};

/**
 * Check if a document is currently approved.
 */
export const isDocumentApproved = (documentData) => {
  if (!documentData) return false;
  return (
    (documentData.status === 'approved' || documentData.status === 'exported') &&
    !!documentData.approvedAt &&
    documentData.approvalRevoked !== true
  );
};

/**
 * Check if a document was modified after approval (warning mode).
 */
export const isModifiedAfterApproval = (documentData) => {
  return isDocumentApproved(documentData) && documentData.modifiedAfterApproval === true;
};

/**
 * Get the list of sections that still require review before approval.
 * Returns an array of dataField names that haven't been viewed yet.
 */
export const getUnreviewedSections = (documentData, approvalConfig, currentSections) => {
  if (!approvalConfig?.enabled || !approvalConfig?.requiredReviewSections?.length) {
    return [];
  }

  const reviewed = documentData?.reviewedSections || {};
  const availableDataFields = currentSections
    ?.filter(s => s.dataField)
    .map(s => s.dataField) || [];

  return approvalConfig.requiredReviewSections.filter(
    (field) => !reviewed[field]?.viewedAt && availableDataFields.includes(field)
  );
};

/**
 * Check if all required sections have been reviewed and the document can be approved.
 */
export const canApproveDocument = (documentData, approvalConfig, currentSections) => {
  if (!documentData) return false;

  // Must be at least completed
  const validStatuses = ['completed', 'approved', 'exported'];
  if (!validStatuses.includes(documentData.status)) return false;

  // If approval is not enabled, always allow
  if (!approvalConfig?.enabled) return true;

  // Check all required sections are reviewed
  const unreviewed = getUnreviewedSections(documentData, approvalConfig, currentSections);
  return unreviewed.length === 0;
};

/**
 * Approve a document. Sets status, timestamp, and user.
 */
export const approveDocument = async (patientId, docId, userId, documentData) => {
  const updatePayload = {
    status: 'approved',
    approvedAt: serverTimestamp(),
    approvedBy: userId,
    approvalRevoked: false,
    modifiedAfterApproval: false,
  };

  await updateData(`patients/${patientId}/documents`, docId, updatePayload);

  logAuditEventAsync(AuditActions.DOCUMENT_APPROVE, {
    resourceType: 'document',
    resourceId: docId,
    patientId,
    metadata: {
      previousStatus: documentData?.status,
    },
  });

  return updatePayload;
};

/**
 * Unapprove a document. Reverts to completed status.
 */
export const unapproveDocument = async (patientId, docId, userId, documentData) => {
  const updatePayload = {
    status: 'completed',
    approvedAt: null,
    approvedBy: null,
    approvalRevoked: true,
    modifiedAfterApproval: false,
  };

  await updateData(`patients/${patientId}/documents`, docId, updatePayload);

  logAuditEventAsync(AuditActions.DOCUMENT_UNAPPROVE, {
    resourceType: 'document',
    resourceId: docId,
    patientId,
    metadata: {
      previousStatus: documentData?.status,
      unapprovedBy: userId,
    },
  });

  return updatePayload;
};

/**
 * Record that a section has been viewed by the user.
 * Uses dot notation to update a single field within reviewedSections.
 */
export const recordSectionView = async (patientId, docId, dataField, userId) => {
  const fieldPath = `reviewedSections.${dataField}`;
  await updateData(`patients/${patientId}/documents`, docId, {
    [fieldPath]: {
      viewedAt: serverTimestamp(),
      viewedBy: userId,
    },
  });
};

/**
 * Handle post-approval edit behavior.
 * Called when a user edits a section on an approved document.
 * Returns the fields to merge into the document update, or null if edit should be blocked.
 */
export const handleApprovalOnEdit = async (patientId, docId, documentData, approvalConfig) => {
  if (!isDocumentApproved(documentData)) return {};

  const behavior = approvalConfig?.editBehavior || APPROVAL_EDIT_BEHAVIORS.AUTO_UNAPPROVE;

  switch (behavior) {
    case APPROVAL_EDIT_BEHAVIORS.AUTO_UNAPPROVE:
      // Revoke approval — document goes back to completed
      const unapprovePayload = {
        status: 'completed',
        approvedAt: null,
        approvedBy: null,
        approvalRevoked: true,
        modifiedAfterApproval: false,
      };
      await updateData(`patients/${patientId}/documents`, docId, unapprovePayload);
      return unapprovePayload;

    case APPROVAL_EDIT_BEHAVIORS.WARNING:
      // Keep approved but flag as modified
      const warningPayload = { modifiedAfterApproval: true };
      await updateData(`patients/${patientId}/documents`, docId, warningPayload);
      return warningPayload;

    case APPROVAL_EDIT_BEHAVIORS.LOCK:
      // Block the edit — caller should prevent save
      return null;

    default:
      return {};
  }
};

/**
 * Get approval fields to include when exporting a document.
 * Export auto-approves the document if not already approved.
 */
export const getApprovalFieldsForExport = (documentData, userId) => {
  return {
    approvedAt: documentData?.approvedAt || serverTimestamp(),
    approvedBy: documentData?.approvedBy || userId,
    approvalRevoked: false,
    modifiedAfterApproval: false,
  };
};

/**
 * Check if editing should be locked (approval lock mode).
 */
export const isEditingLocked = (documentData, approvalConfig) => {
  if (!isDocumentApproved(documentData)) return false;
  return approvalConfig?.editBehavior === APPROVAL_EDIT_BEHAVIORS.LOCK;
};

/**
 * Split sections into primary and secondary groups based on tab property.
 */
export const splitSectionsByTab = (sections) => {
  if (!sections) return { primary: [], secondary: [] };
  
  const primary = [];
  const secondary = [];

  for (const section of sections) {
    if (section.tab === 'secondary') {
      secondary.push(section);
    } else {
      primary.push(section);
    }
  }

  return { primary, secondary };
};
